import { createContext, useContext, useState, ReactNode } from 'react';
import translations, { Language, CommonTranslations, PersonEditTranslations, NavTranslations, PersonTranslations, InvoiceTranslations, ArticleTranslations, InvoiceSettingsTranslations, MemberProfileTranslations, MemberAccountTranslations, TAccountTranslations, HomeTranslations } from '../i18n/translations';

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: {
    common: CommonTranslations;
    personEdit: PersonEditTranslations;
    nav: NavTranslations;
    person: PersonTranslations;
    invoice: InvoiceTranslations;
    article: ArticleTranslations;
    invoiceSettings: InvoiceSettingsTranslations;
    memberProfile: MemberProfileTranslations;
    memberAccount: MemberAccountTranslations;
    tAccount: TAccountTranslations;
    home: HomeTranslations;
  };
};

const LanguageContext = createContext<LanguageContextType>({
  language: 'de',
  setLanguage: () => {},
  t: {
    common: translations.de.common,
    personEdit: translations.de.personEdit,
    nav: translations.de.nav,
    person: translations.de.person,
    invoice: translations.de.invoice,
    article: translations.de.article,
    invoiceSettings: translations.de.invoiceSettings,
    memberProfile: translations.de.memberProfile,
    memberAccount: translations.de.memberAccount,
    tAccount: translations.de.tAccount,
    home: translations.de.home,
  },
});

export const useLanguage = () => useContext(LanguageContext);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('cm_language');
    return (stored === 'de' || stored === 'en') ? stored : 'de';
  });

  const setLanguage = (lang: Language) => {
    localStorage.setItem('cm_language', lang);
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t: {
        common: translations[language].common,
        personEdit: translations[language].personEdit,
        nav: translations[language].nav,
        person: translations[language].person,
        invoice: translations[language].invoice,
        article: translations[language].article,
        invoiceSettings: translations[language].invoiceSettings,
        memberProfile: translations[language].memberProfile,
        memberAccount: translations[language].memberAccount,
        tAccount: translations[language].tAccount,
        home: translations[language].home,
      },
    }}>
      {children}
    </LanguageContext.Provider>
  );
}
